import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const DETAIL_I18N_PREFIX = 'features.invoices.detail';
const FIELD_LABEL_SUBPATH = 'validationBanner.fieldLabels';
const LINE_PATH_RE = /^invoiceLines\.\d+\.?/;

/**
 * Translates raw validation field paths (e.g. "invoiceLines.0.totalAmountNoVat") into human
 * labels using the same fieldLabels map as the invoice validation banner. Falls back to the
 * raw path when no translation exists.
 */
export function useValidationPathLabel(): (path: string | undefined) => string | undefined {
  const { t, i18n } = useTranslation();

  return useCallback(
    (path: string | undefined): string | undefined => {
      if (!path) return undefined;

      const tryKey = (sub: string): string | undefined => {
        const abs = `${DETAIL_I18N_PREFIX}.${FIELD_LABEL_SUBPATH}.${sub}`;
        if (!i18n.exists(abs)) return undefined;
        const value = t(abs, { returnObjects: true });
        return typeof value === 'string' ? value : undefined;
      };

      // invoiceLines.N.field → translate the sub-field, prefix with "Line item"
      const lineMatch = LINE_PATH_RE.test(path);
      const key = lineMatch ? path.replace(LINE_PATH_RE, '') : path;

      const label =
        tryKey(key) ??
        tryKey(`${key}Label`) ??
        (() => {
          const leaf = key.split('.').pop() ?? key;
          return leaf !== key ? tryKey(leaf) : undefined;
        })() ??
        key;

      if (lineMatch) {
        const lineLabel = tryKey('invoiceLines');
        if (!lineLabel) return label;
        return label ? `${lineLabel} › ${label}` : lineLabel;
      }

      return label;
    },
    [t, i18n]
  );
}
